import React, { useEffect, useState } from "react";
import { fetchForgeScenarios, runStressTest } from "@/lib/forgeApi";
import { AlertTriangle, Zap, RefreshCw } from "lucide-react";
import { toast } from "sonner";

const fmt$ = (n) => {
  if (n == null) return "—";
  const sign = n >= 0 ? "+" : "−";
  const abs = Math.abs(n);
  if (abs >= 100) return `${sign}$${Math.round(abs).toLocaleString()}`;
  return `${sign}$${abs.toFixed(2)}`;
};

export const StressTestPanel = ({ legs, spot, iv, r = 0.045 }) => {
  const [scenarios, setScenarios] = useState([]);
  const [running, setRunning] = useState(null);
  const [results, setResults] = useState({});
  const [customDrop, setCustomDrop] = useState(0.1);
  const [customIv, setCustomIv] = useState(0.3);
  const [customResult, setCustomResult] = useState(null);

  useEffect(() => {
    fetchForgeScenarios()
      .then((d) => setScenarios(d.scenarios || []))
      .catch(() => setScenarios([]));
  }, []);

  // Reset results when underlying changes
  useEffect(() => {
    setResults({});
    setCustomResult(null);
  }, [JSON.stringify(legs), spot, iv]);

  const runScenario = async (sc) => {
    if (!legs?.length || !spot) return;
    setRunning(sc.id);
    try {
      const res = await runStressTest({
        legs,
        spot,
        iv,
        r,
        price_drop_pct: sc.price_drop_pct,
        iv_spike_abs: sc.iv_spike_abs,
      });
      setResults((prev) => ({ ...prev, [sc.id]: res }));
    } catch (e) {
      console.error("stress run failed", e);
      toast.error("Stress test failed");
    } finally {
      setRunning(null);
    }
  };

  const runCustom = async () => {
    if (!legs?.length || !spot) return;
    setRunning("custom");
    try {
      const res = await runStressTest({
        legs,
        spot,
        iv,
        r,
        price_drop_pct: Number(customDrop),
        iv_spike_abs: Number(customIv),
      });
      setCustomResult(res);
    } catch (e) {
      toast.error("Custom shock failed");
    } finally {
      setRunning(null);
    }
  };

  return (
    <div
      className="bg-[#0c0c0e] border border-white/10 rounded-xl p-4 sm:p-5"
      data-testid="stress-test-panel"
    >
      <div className="flex items-center gap-2 mb-3">
        <AlertTriangle className="w-4 h-4 text-[#FF7A33]" />
        <h3 className="text-sm font-medium tracking-tight">Black Swan Stress-Tester</h3>
      </div>
      <p className="text-[11px] text-white/55 leading-relaxed mb-4">
        Re-prices every leg under a simultaneous spot drop + IV spike — full Black-Scholes, no
        linear Greek approximations. The number you see is what the position is worth on day of shock.
      </p>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 mb-4">
        {scenarios.map((sc) => {
          const res = results[sc.id];
          const pnl = res?.aggregate_pnl;
          const positive = pnl != null && pnl >= 0;
          return (
            <button
              key={sc.id}
              type="button"
              onClick={() => runScenario(sc)}
              disabled={running === sc.id || !legs?.length}
              data-testid={`stress-scenario-${sc.id}`}
              className={`text-left rounded-lg px-3 py-2.5 border transition ${
                res
                  ? positive
                    ? "border-[#00C805]/40 bg-[#00C805]/5"
                    : "border-[#FF3B30]/40 bg-[#FF3B30]/5"
                  : "border-white/10 bg-[#0a0a0c] hover:border-white/25"
              } ${!legs?.length ? "opacity-40 cursor-not-allowed" : ""}`}
            >
              <div className="flex items-center justify-between gap-2 mb-1">
                <span className="text-[10px] uppercase tracking-[0.18em] text-white/60">
                  {sc.name.split(" · ")[0]}
                </span>
                {running === sc.id && <RefreshCw className="w-3 h-3 animate-spin text-white/50" />}
              </div>
              <div className="text-[10px] text-white/50 mb-1.5">
                {sc.name.split(" · ")[1] || ""}
              </div>
              <div
                className={`num font-medium ${
                  pnl == null ? "text-white/50" : positive ? "text-[#00C805]" : "text-[#FF3B30]"
                }`}
              >
                {pnl == null ? "Tap to test" : fmt$(pnl)}
              </div>
              {res && (
                <div className="text-[9px] text-white/40 mt-1 uppercase tracking-[0.16em]">
                  → spot ${res.scenario.shocked_spot} · iv {Math.round(res.scenario.shocked_iv * 100)}%
                </div>
              )}
            </button>
          );
        })}
      </div>

      {/* Custom shock */}
      <div className="rounded-lg border border-white/10 bg-[#0a0a0c] p-3">
        <div className="text-[10px] uppercase tracking-[0.22em] text-white/45 mb-2 flex items-center gap-1.5">
          <Zap className="w-3 h-3 text-[#FFD60A]" /> Custom shock
        </div>
        <div className="grid grid-cols-2 gap-3 mb-2">
          <label className="text-[10px] text-white/55">
            Spot move ({(customDrop * 100).toFixed(0)}%, negative = rally)
            <input
              type="range"
              min="-0.30"
              max="0.50"
              step="0.01"
              value={customDrop}
              onChange={(e) => setCustomDrop(parseFloat(e.target.value))}
              className="w-full accent-[#FF7A33] mt-1"
              data-testid="stress-custom-drop"
            />
          </label>
          <label className="text-[10px] text-white/55">
            IV spike (+{(customIv * 100).toFixed(0)} pts)
            <input
              type="range"
              min="-0.20"
              max="1.00"
              step="0.01"
              value={customIv}
              onChange={(e) => setCustomIv(parseFloat(e.target.value))}
              className="w-full accent-[#FFD60A] mt-1"
              data-testid="stress-custom-iv"
            />
          </label>
        </div>
        <div className="flex items-center justify-between gap-3">
          <button
            type="button"
            onClick={runCustom}
            disabled={running === "custom" || !legs?.length}
            data-testid="stress-custom-run"
            className="text-[11px] bg-white text-black px-3 py-1.5 rounded-md font-medium hover:bg-white/90 disabled:opacity-40 inline-flex items-center gap-1.5"
          >
            {running === "custom" && <RefreshCw className="w-3 h-3 animate-spin" />} Run shock
          </button>
          {customResult && (
            <span
              className={`num text-sm font-medium ${
                customResult.aggregate_pnl >= 0 ? "text-[#00C805]" : "text-[#FF3B30]"
              }`}
            >
              {fmt$(customResult.aggregate_pnl)}
            </span>
          )}
        </div>
      </div>

      {!legs?.length && (
        <div className="mt-3 text-[10px] text-white/45 italic">
          Build a strategy first to enable stress tests.
        </div>
      )}
    </div>
  );
};

export default StressTestPanel;
